import React from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { useSeason } from '../../contexts/SeasonContext';
import { useNavigate, useLocation } from 'react-router-dom';

interface SeasonSelectorProps {
  onSeasonChange?: (year: number) => void;
  showLabel?: boolean;
  className?: string;
}

const SeasonSelector: React.FC<SeasonSelectorProps> = ({ 
  onSeasonChange, 
  showLabel = true,
  className = '' 
}) => {
  const { currentSeason, setCurrentSeason, availableSeasons } = useSeason();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSeasonChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const year = parseInt(e.target.value);
    setCurrentSeason(year);
    
    // Call custom handler if provided
    if (onSeasonChange) {
      onSeasonChange(year);
    } else {
      // Default behavior: update URL if on a detail page
      const pathParts = location.pathname.split('/');
      
      // Check if we're on a team detail page with year
      if (pathParts[1] === 'teams' && pathParts[2] && pathParts[3]) {
        // Replace the year in the URL
        navigate(`/teams/${pathParts[2]}/${year}`);
      }
      // Add more page-specific routing as needed
    }
  };

  // Generate display text for seasons
  const getSeasonDisplay = (year: number) => {
    // For sports that span years (NFL, NBA, NHL)
    const nextYear = (year + 1).toString().slice(2);
    return `${year}-${nextYear}`;
  };

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      {showLabel && (
        <div className="flex items-center space-x-1 text-gray-400">
          <Calendar className="w-4 h-4" />
          <span className="text-sm">Season:</span>
        </div>
      )}
      
      <div className="relative">
        <select
          value={currentSeason}
          onChange={handleSeasonChange}
          className="
            appearance-none
            bg-surface 
            border border-border 
            rounded-lg 
            px-4 py-2 pr-10
            text-white 
            text-sm
            font-medium
            focus:outline-none 
            focus:border-primary
            hover:border-primary/50
            transition-colors
            cursor-pointer
          "
        >
          {availableSeasons.slice(0, 20).map((year) => (
            <option key={year} value={year}>
              {getSeasonDisplay(year)} Season
            </option>
          ))}
        </select>
        
        <ChevronDown className="
          absolute 
          right-3 
          top-1/2 
          -translate-y-1/2 
          w-4 h-4 
          text-gray-400 
          pointer-events-none
        " />
      </div>
    </div>
  );
};

export default SeasonSelector;